import React, { ReactElement, useState, useEffect } from "react";

import { getUserAnswers, updatePollResult } from "../../../apis/firebase";

import apiRequest from "../../../apis/request";

// types
import { PollRankingType } from "../../../types";

import { useTranslation } from "react-i18next";

const Ranking = (props: PollRankingType): ReactElement => {
  const [loading, setIsLoading] = useState(false);
  const [response, setIsResponse] = useState(false);
  const [result, setResult] = useState<any>({});
  const [choices, setChoices] = useState([]);

  const [userPollInfo, setUserPollInfo] = useState<any>([]);

  const [selectedChoices, setSelectedChoices] = useState([]);
  const { t } = useTranslation();

  const {
    canEditAnswer,
    question,
    randomize,
    minRequiredChoices,
    showResults,
    sessionId,
    pollId,
    trackId,
    choices: _choices,
  } = props;

  useEffect(() => {
    let _data = [..._choices];
    if (randomize) {
      _data = _data.sort(() => Math.random() - 0.5);
    }
    setChoices(_data);
  }, [_choices]);

  useEffect(() => {
    (async () => {
      setUserPollInfo(await getUserAnswers(pollId, sessionId));

      updatePollResult(pollId, sessionId, (data) => {
        if (data && data?.answers) {
          let totalAns = data.totalNumOfAnswers;
          let _answers = Object.entries(data?.answers);
          _answers = _answers?.map((d) => ({
            value: d[0],
            score: d[1],
          }));

          _answers = _answers.sort((a, b) => b.score - a.score);

          const sumOfScores = _answers.reduce((acc, b) => {
            return acc + b.score;
          }, 0);

          if (sumOfScores > 0) {
            _answers =
              (_answers &&
                _answers.map((d) => {
                  d.percentage = parseFloat(
                    (d.score / sumOfScores) * 100
                  ).toFixed(2);
                  return d;
                })) ||
              [];
          }

          setResult({ answers: _answers });
        }
      });
    })();
  }, []);

  useEffect(() => {
    if (userPollInfo) {
      setIsLoading(false);
      setIsResponse(userPollInfo.length > 1);
    }
  }, [userPollInfo]);

  const handleSubmit = async () => {
    if (selectedChoices.length < minRequiredChoices) return;

    setIsLoading(true);
    try {
      const payload = {
        pollId,
        sessionId,
        trackId,
        answer: selectedChoices,
      };

      const { status } = await apiRequest("POST", "polls/ranking", [], payload);

      if (status === 200) {
        setIsLoading(false);
        setIsResponse(true);
        // setUserPollInfo([payload]);
      } else {
        setIsResponse(false);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleReset = () => {
    setIsLoading(false);
    setIsResponse(false);
  };

  const handleSelectChoice = (choice) => {
    const _choices = [...selectedChoices];
    _choices.push(choice);

    setSelectedChoices(_choices);
  };

  const handleDeSelectChoice = (choice) => {
    let _choices = [...selectedChoices];
    _choices = _choices.filter((d) => d !== choice);
    setSelectedChoices(_choices);
  };

  const handleStart = (e, d) => {
    e.stopPropagation();
    e.target.classList.add("row-dragging");
    e.dataTransfer.setData("ranking", d);
  };

  const handleLeave = (e) => {
    e.target.classList.remove("row-dragging");
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const ranking = e.dataTransfer.getData("ranking");
    const index = e.dataTransfer.getData("index");
    ranking ? handleSelectChoice(ranking) : "";
    index ? handleAlign(e, index) : "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDeStart = (e, i) => {
    e.stopPropagation();
    e.dataTransfer.setData("index", i);
  };

  const handleAlign = (e, i) => {
    let _choices = [...selectedChoices];
    let _item = selectedChoices[i];
    let dropItem = selectedChoices.indexOf(e.target.innerText);
    _choices.splice(i, 1);
    _choices.splice(dropItem, 0, _item);
    setSelectedChoices(_choices);
  };

  return (
    <div className="tm__polls">
      {/* Loader - Start */}
      {loading && (
        <div className="tm__polls-loader d-flex justify-content-center align-items-center">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      )}
      {/* Loader - End */}

      <div className="tm__polls-head tm__polls-head-divider">
        <label className="fs-7 text-primary mb-0">{question}</label>
      </div>
      <div className="tm__polls-body">
        {/* Options - Start */}
        {!response && (
          <>
            <ul
              className="list-style-none p-0 m-0"
              onDrop={(e) => handleDrop(e)}
              onDragOver={(e) => handleDragOver(e)}>
              {!!selectedChoices &&
                selectedChoices.map((d, i) => (
                  <li
                    draggable
                    key={i}
                    onClick={() => handleDeSelectChoice(d)}
                    onDragStart={(e) => handleDeStart(e, i)}
                    className="mb-2">
                    <div className="form-check selected">
                      <i className="custom-rank d-flex align-items-center justify-content-center">
                        {i + 1}
                      </i>
                      <label
                        className="form-check-label fs-7"
                        htmlFor="multipleChoice1">
                        {d}
                      </label>
                    </div>
                  </li>
                ))}
              {choices.length != selectedChoices.length && (
                <li className="d-flex align-items-center justify-content-center form-outline-box mb-2">
                  <label className="fs-8 m-0">
                    {t("POLLS_RANKING_HINT_MESSAGE")}
                  </label>
                </li>
              )}
            </ul>
            <ul className="list-style-none p-0 m-0">
              {!!choices &&
                choices
                  .filter((d) => selectedChoices.indexOf(d) < 0)
                  .map((d, i) => (
                    <li
                      draggable
                      key={i}
                      onClick={() => handleSelectChoice(d)}
                      onDragStart={(e) => handleStart(e, d)}
                      onDragEnd={(e) => handleLeave(e)}
                      className="mb-2">
                      <div className="form-check">
                        <i className="custom-rank d-flex align-items-center justify-content-center">
                          {selectedChoices.length + 1}
                        </i>
                        <label
                          className="form-check-label fs-7"
                          htmlFor="multipleChoice1">
                          {d}
                        </label>
                      </div>
                    </li>
                  ))}
            </ul>
          </>
        )}
        {/* Options - End */}

        {/* Response - Start */}
        {response && showResults && (
          <ul className="list-style-none p-0 m-0">
            {result.answers &&
              result.answers.map((ans, i) => {
                return (
                  <li key={i} className="mb-4">
                    <div className="form-progress">
                      <label className="fs-8">{ans.value}</label>
                      <div className="progress">
                        <div
                          className="progress-bar  bg-primary"
                          role="progressbar"
                          style={{ width: `${ans.percentage}%` }}></div>
                        <small>{ans.percentage}%</small>
                      </div>
                    </div>
                  </li>
                );
              })}
          </ul>
        )}

        {response && !showResults && (
          <div className="tm__polls-body">
            {/* Response - Start */}
            <div className="d-flex flex-column align-items-center">
              <i className="bi bi-check-circle-fill text-primary fs-1"></i>
              <span>{t("POLLS_THANK_YOU_FOR_ANSWER")}</span>
            </div>
            {/* Response - End */}
          </div>
        )}
        {/* Response - End */}

        {/* Warning - Start */}
        {!response && (
          <>
            {minRequiredChoices > 1 && (
              <span className="d-block fs-7 text-warning mt-3">
                {t("POLLS_HINT_TEXT_SELECT")} {t("POLLS_HINT_TEXT_ATLEAST")}{" "}
                {minRequiredChoices} {t("POLLS_HINT_TEXT_ANSWERS")}
              </span>
            )}
          </>
        )}
        {/* Warning - End */}
      </div>

      <div className="tm__polls-footer tm__polls-footer-divider d-flex justify-content-center">
        {!response && (
          <button
            type="button"
            className="btn btn-primary btn-small"
            onClick={() => {
              handleSubmit();
            }}
            disabled={selectedChoices.length < minRequiredChoices}>
            {t("POLLS_SUBMIT_TEXT")}
          </button>
        )}
        {response && canEditAnswer && (
          <button
            type="button"
            className="btn btn-primary btn-small"
            onClick={() => {
              handleReset();
            }}>
            {t("POLLS_EDIT_RESPONSE")}
          </button>
        )}
      </div>
    </div>
  );
};

export default Ranking;
